import React, { useEffect } from "react";
import { useSiteCore } from "../../../../../../hooks/useSiteCore";
import { invoiceHistorySiteCoreKeys } from "../../../../siteCore/SiteCoreKeys";
import {
  FormController,
  MuiBox,
  MuiDateFieldFormController,
  MuiGrid,
  MuiSelect,
  MuiTextField,
  MuiTypography,
} from "../../../../../../components";
import {
  getValueOrBlank,
  replaceHashWithValue,
} from "../../../../../../helpers/utils";
import { pageId } from "../../../../../createUser/CreateUser";
import { InvoiceReversalFormKey } from "../../../../modelsAndSchemas/Models";
import { useNavigationSiteCore } from "../../../../../../hooks/useNavigationSiteCore";
import dayjs from "dayjs";
import { InvoiceReversalContainer } from "./invoiceReversal.style";
import { useFormContext, useWatch } from "react-hook-form";
import {
  INVOICE_REVERSAL_REASON_OTHERS_SITE_CORE_KEY,
  MAX_LENGTH_100,
} from "../../../../../../helpers/constants";
import { useGridContainer } from "../../../../../../hooks/useGridContainer";
import { useGetLoggedInUserDetails } from "../../../../../../aAdminTabTeam2/hooksAndFunctions/customHooks/useGetLoggedInUserDetails";
import { getDateFormatByCountry } from "../../../../../../helpers/constants/countryDateFormat";

type InvoiceReversalProps = {
  invoiceId: string;
};

const InvoiceReversal: React.FC<InvoiceReversalProps> = ({ invoiceId }) => {
  const {
    fetchLabel,
    commonLabels,
    fetchDropdownValues,
    commonValidationMessages,
  } = useSiteCore();
  const { commonARIALabel } = useNavigationSiteCore();
  const { gridSizeLarge } = useGridContainer(true);
  const { getValues, control, setValue } = useFormContext();
  const reversalReason = useWatch({
    control,
    name: InvoiceReversalFormKey.reversalReason,
  });
  const { loggedInUser } = useGetLoggedInUserDetails();
  const userCountryDateFormat = getDateFormatByCountry(
    getValueOrBlank(loggedInUser?.countryCode),
  );

  const getLabel = (key: keyof typeof invoiceHistorySiteCoreKeys) => {
    return fetchLabel(
      invoiceHistorySiteCoreKeys.invoiceHistoryTitle,
      invoiceHistorySiteCoreKeys[key],
    );
  };

  useEffect(() => {
    if (reversalReason !== INVOICE_REVERSAL_REASON_OTHERS_SITE_CORE_KEY) {
      setValue(InvoiceReversalFormKey.comments, "");
    }
  }, [reversalReason]);

  return (
    <InvoiceReversalContainer>
      <MuiTypography
        className="invoice-reversal-message"
        component="label"
        variant="h3"
      >
        {replaceHashWithValue(
          getLabel("areYouSureYouWantToReverseTheInvoice"),
          invoiceId,
        )}
      </MuiTypography>
      <MuiBox>
        <MuiGrid container spacing={2}>
          <MuiGrid size={gridSizeLarge}>
            <FormController control={control} name="reversalReason">
              <MuiSelect
                label={getLabel("confirmationPopUpReversalReasonDropDown")}
                options={fetchDropdownValues(
                  invoiceHistorySiteCoreKeys.invoiceHistoryTitle,
                  invoiceHistorySiteCoreKeys.confirmationPopUpInvoiceReversalReasonList,
                )}
                visibleDefaultSelectOption
                value=""
                id={`${pageId}-${InvoiceReversalFormKey.reversalReason}`}
                placeholder={commonLabels.selectPlaceHolder}
                isMandatory
                requiredARIALabel={commonARIALabel.required}
              />
            </FormController>
          </MuiGrid>
          <MuiGrid size={gridSizeLarge}>
            <MuiDateFieldFormController
              control={control}
              name={InvoiceReversalFormKey.reversalDate}
              id={`${pageId}-${InvoiceReversalFormKey.reversalDate}`}
              value={
                getValues(InvoiceReversalFormKey.reversalDate)
                  ? dayjs(getValues(InvoiceReversalFormKey.reversalDate))
                  : dayjs()
              }
              label={getLabel("confirmationPopUpReversalDate")}
              className="date-picker"
              disabled
              disableLabel
              requiredARIALabel={commonARIALabel.required}
              selectDateARIALabel={commonARIALabel.selectDate}
              format={userCountryDateFormat}
            />
          </MuiGrid>
        </MuiGrid>
        {reversalReason === INVOICE_REVERSAL_REASON_OTHERS_SITE_CORE_KEY && (
          <MuiBox className="comments">
            <FormController
              control={control}
              name={InvoiceReversalFormKey.comments}
            >
              <MuiTextField
                id={`${pageId}-${InvoiceReversalFormKey.comments}`}
                label={getLabel("confirmationPopUpCommentOther")}
                placeholder={getLabel(
                  "confirmationPopUpCommentBoxPlaceholderText",
                )}
                className="comments-input"
                multiline
                rows={4}
                data-testid={`${pageId}-${InvoiceReversalFormKey.comments}`}
                isMandatory
                requiredARIALabel={commonARIALabel.required}
                maxLength={MAX_LENGTH_100}
                tooltipMessage={
                  commonValidationMessages.fieldSpecialCharactersTooltipMessage
                }
                tooltipHeaderTitle={
                  commonValidationMessages.allowedSpecialCharactersTitle
                }
              />
            </FormController>
          </MuiBox>
        )}
      </MuiBox>
    </InvoiceReversalContainer>
  );
};

export default InvoiceReversal;
